import { styled } from "goober";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { navEnterStyles } from "../hooks/useNavTransition";
import { usePanelTransition } from "./PanelTransition";
import { playClick } from "../lib/sounds";
import { writeToClipboard } from "../lib/writeToClipboard";
import { AnimatedCopyIcon } from "./AnimatedCopyIcon";
import { Button } from "./Button";
import { ClickButton } from "./ClickButton";
import { Dropdown } from "./Dropdown";
import { ToggleButton } from "./HeadingToggle";
import { Icon, type IconName } from "./Icon";
import { Link } from "./Link";
import { ShortcutHint } from "./ShortcutHint";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { UrlDisplay } from "./UrlDisplay";

export type BrowserLinkItem = {
  key: string;
  icon: IconName;
  label: string;
  metadata: string;
  selected: boolean;
  onClick: () => void;
};

type Props = {
  displayUrl: string;
  openUrl?: string;
  links?: ReadonlyArray<BrowserLinkItem>;
  onReload?: () => void;
  onBack?: () => void;
  onForward?: () => void;
  canBack?: boolean;
  canForward?: boolean;
  inspectMode?: boolean;
  onInspectModeChange?: (mode: boolean) => void;
  embedded?: boolean;
  access?: ReactNode;
  children: ReactNode;
};

const StyledBrowserChrome = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;

  &[data-embedded="true"] {
    background: var(--chatpad-background, var(--surface-secondary));
    border: none;
    border-radius: 0;
  }
`;

const Container = styled("div")`
  ${navEnterStyles}
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 8px;
  flex-shrink: 0;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const UrlBar = styled("div")`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 0 10px;
  border-radius: 9999px;
  background: var(--alpha4);
  min-width: 0;
  box-sizing: border-box;

  &:hover {
    background: var(--alpha8);
  }

  &:hover [data-url-actions],
  &:has(:focus-visible) [data-url-actions] {
    opacity: 1;
  }
`;

const UrlSlot = styled("div")`
  flex: 1;
  min-width: 0;
`;

const UrlText = styled("div")`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  text-align: center;
  font-family: inherit;
  font-weight: 450;
  font-size: 10px;
  line-height: 12px;
`;

const UrlActions = styled("div")`
  position: relative;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  padding: 0 1px;
  margin-right: -8px;
  opacity: 0;

  > .tint {
    position: absolute;
    top: 0;
    left: 0;
    width: 20px;
    height: 20px;
    border-radius: 9999px;
    background: var(--alpha8);
    opacity: 0;
    pointer-events: none;
    transition: transform var(--motion-press) var(--ease-out);
  }

  &:has([data-url-action]:hover) > .tint,
  &:has([data-url-action]:focus-visible) > .tint {
    opacity: 1;
  }

  &:has([data-url-action="open"]:hover) > .tint,
  &:has([data-url-action="open"]:focus-visible) > .tint {
    transform: translateX(18px);
  }

  & [data-url-action] {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 18px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 9999px;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;

    &:hover {
      color: var(--text-primary);
    }
  }
`;

const FrameWrap = styled("div")`
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
  justify-content: center;
`;

// While a dropdown is open, the preview iframe would otherwise swallow clicks (an
// iframe never dispatches mousedown to the parent document), so clicking the page
// couldn't dismiss the menu. This transparent cover keeps that click in the parent
// document, where the dropdown's outside-click detection sees it.
const IframeCover = styled("div")`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const MOBILE_VIEWPORT_WIDTH = 390;

const Viewport = styled("div")`
  ${navEnterStyles}
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  transition:
    width var(--motion-base) var(--ease-out),
    height var(--motion-base) var(--ease-out),
    margin var(--motion-base) var(--ease-out);

  &[data-viewport="mobile"] {
    width: ${MOBILE_VIEWPORT_WIDTH}px;
    max-width: 100%;
    height: calc(100% - 24px);
    margin: 12px 0;
    border: 0.5px solid var(--border-alpha);
    border-radius: 12px;
    overflow: hidden;
    background: var(--surface-primary);
    box-shadow: var(--elevation-low);
  }
`;

const InspectButton = styled(ToggleButton)`
  width: auto;
  gap: 6px;
  padding: 0 8px;

  &[aria-pressed="true"] {
    background: var(--alpha4);
  }
`;

const ViewportButton = styled(ToggleButton)`
  &[aria-pressed="true"] {
    background: var(--alpha4);
  }
`;

function CopyLinkButton({ openUrl }: { openUrl: string }) {
  const [copied, setCopied] = useState(false);
  const copiedResetTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useEffect(() => {
    return () => {
      if (copiedResetTimeout.current) clearTimeout(copiedResetTimeout.current);
    };
  }, []);

  const copyLink = () => {
    void writeToClipboard(openUrl);
    setCopied(true);
    if (copiedResetTimeout.current) clearTimeout(copiedResetTimeout.current);
    copiedResetTimeout.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <ClickButton
      type="button"
      aria-label="Copy link"
      data-no-pill=""
      data-url-action="copy"
      data-copied={copied || undefined}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        copyLink();
      }}
    >
      <AnimatedCopyIcon copied={copied} size={10} />
    </ClickButton>
  );
}

export function BrowserChrome({
  displayUrl,
  openUrl,
  links,
  onReload,
  onBack,
  onForward,
  canBack,
  canForward,
  inspectMode,
  onInspectModeChange,
  embedded,
  access,
  children,
}: Props) {
  const direction = usePanelTransition();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");

  const urlBar = (
    <UrlBar
      onMouseDown={(e) => {
        if (e.button !== 0 || !e.metaKey || !openUrl) return;
        e.stopPropagation();
        playClick();
        window.open(openUrl, "_blank", "noopener,noreferrer");
      }}
    >
      {access}
      <UrlText>
        <UrlDisplay url={displayUrl} />
      </UrlText>
      {openUrl && (
        <UrlActions data-url-actions="">
          <span className="tint" aria-hidden="true" />
          <CopyLinkButton openUrl={openUrl} />
          <Tooltip content="Open in new tab">
            <Link
              data-url-action="open"
              to={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in new tab"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="arrow-up-right" size={10} />
            </Link>
          </Tooltip>
        </UrlActions>
      )}
    </UrlBar>
  );

  return (
    <StyledBrowserChrome data-embedded={embedded}>
      <Container data-nav-enter={direction}>
        <Button
          variant="secondary"
          icon="arrow-left"
          type="button"
          onClick={onBack}
          disabled={!onBack || !canBack}
          aria-label="Back"
        />
        <Button
          variant="secondary"
          icon="arrow-right"
          type="button"
          onClick={onForward}
          disabled={!onForward || !canForward}
          aria-label="Forward"
        />
        <Button
          variant="secondary"
          icon="rotate-cw"
          type="button"
          onClick={onReload}
          disabled={!onReload}
          aria-label="Reload"
        />
        <UrlSlot>
          {links && links.length > 0 ? (
            <Dropdown
              align="bottom-center"
              minWidth={320}
              margin="6px 0 0"
              onOpenChange={setDropdownOpen}
              opener={urlBar}
              sections={[
                {
                  items: links.map((link) => ({
                    type: "button" as const,
                    key: link.key,
                    selected: link.selected,
                    left: <Icon name={link.icon} size={16} />,
                    label: link.label,
                    subtitle: (
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {link.metadata}
                      </Text>
                    ),
                    right: link.selected ? (
                      <Icon
                        name="check"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    ) : null,
                    onClick: link.onClick,
                  })),
                },
              ]}
            />
          ) : (
            urlBar
          )}
        </UrlSlot>
        <Tooltip content="Mobile view">
          <ViewportButton
            aria-pressed={viewport === "mobile"}
            aria-label="Toggle mobile view"
            onPress={() =>
              setViewport((v) => (v === "mobile" ? "desktop" : "mobile"))
            }
          >
            <Icon name="smartphone" size={12} />
          </ViewportButton>
        </Tooltip>
        <Tooltip
          content={
            <>
              Point and prompt <ShortcutHint>⇧⌥I</ShortcutHint>
            </>
          }
        >
          <InspectButton
            aria-pressed={!!inspectMode}
            aria-label="Toggle inspect"
            disabled={!onInspectModeChange}
            onPress={() => onInspectModeChange?.(!inspectMode)}
          >
            <Icon name="mouse-pointer-2" size={12} />
            <Text variant="text-small-md">Inspect</Text>
          </InspectButton>
        </Tooltip>
      </Container>
      <FrameWrap>
        <Viewport data-nav-enter={direction} data-viewport={viewport}>
          {children}
        </Viewport>
        {dropdownOpen && <IframeCover />}
      </FrameWrap>
    </StyledBrowserChrome>
  );
}
